export type EasterThemeMode = 'good-friday' | 'easter-sunday' | 'independence-day';

export interface EasterTheme {
  isActive: boolean;
  mode: EasterThemeMode | null;
  bannerEyebrow: string;
  bannerTitle: string;
  bannerMessage: string;
  pageGreeting: string;
  pageAccent: string;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const BUSINESS_TIME_ZONE = 'Africa/Lagos';

// Nigeria's Independence Day: 1 October, every year (first celebrated in 1960).
const INDEPENDENCE_MONTH = 10;
const INDEPENDENCE_DAY = 1;
const INDEPENDENCE_YEAR = 1960;

const getOrdinal = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const getBusinessDateParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);

  return { year, month, day };
};

const getDateKey = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getWesternEasterSunday = (year: number) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(Date.UTC(year, month - 1, day));
};

export const getGoodFriday = (year: number) => {
  const easterSunday = getWesternEasterSunday(year);
  return new Date(easterSunday.getTime() - DAY_IN_MS * 2);
};

export const getEasterTheme = (date = new Date()): EasterTheme => {
  const { year, month, day } = getBusinessDateParts(date);
  const todayKey = `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
  const goodFridayKey = getDateKey(getGoodFriday(year));
  const easterSundayKey = getDateKey(getWesternEasterSunday(year));

  // Active only for the whole of 1 October (Lagos time); reverts automatically at midnight.
  if (month === INDEPENDENCE_MONTH && day === INDEPENDENCE_DAY) {
    const anniversary = getOrdinal(year - INDEPENDENCE_YEAR);
    return {
      isActive: true,
      mode: 'independence-day',
      bannerEyebrow: 'Independence Day at Immersia',
      bannerTitle: `Happy ${anniversary} Independence Day, Nigeria!`,
      bannerMessage: 'Celebrating unity, freedom, and the green-white-green spirit with every game today.',
      pageGreeting: 'Happy Independence Day from Immersia.',
      pageAccent: 'Proudly Nigerian, celebrating in green and white all day.',
    };
  }

  if (todayKey >= goodFridayKey && todayKey < easterSundayKey) {
    return {
      isActive: true,
      mode: 'good-friday',
      bannerEyebrow: 'Good Friday at Immersia',
      bannerTitle: 'A calm pause before the celebration',
      bannerMessage: 'Reflect, recharge, and enjoy a peaceful holiday experience with friends and family.',
      pageGreeting: 'Good Friday blessings from Immersia.',
      pageAccent: 'Reflective holiday offers and warm service today.',
    };
  }

  if (todayKey === easterSundayKey) {
    return {
      isActive: true,
      mode: 'easter-sunday',
      bannerEyebrow: 'Easter Sunday at Immersia',
      bannerTitle: 'Happy Easter from all of us',
      bannerMessage: 'Celebrate the joy of Easter with bright moments, playful energy, and memorable family fun.',
      pageGreeting: 'Happy Easter from Immersia.',
      pageAccent: 'Fresh colors, festive cheer, and joyful play all day.',
    };
  }

  return {
    isActive: false,
    mode: null,
    bannerEyebrow: '',
    bannerTitle: '',
    bannerMessage: '',
    pageGreeting: '',
    pageAccent: '',
  };
};

export const getEasterThemeClassName = (mode: EasterThemeMode | null) =>
  mode ? `easter-theme easter-theme--${mode}` : '';

export const getEasterWindow = (year: number) => ({
  goodFriday: getGoodFriday(year),
  easterSunday: getWesternEasterSunday(year),
});
